import { useEffect, useRef, useState } from 'react';
import {
    ThumbDown,
    ThumbDownOutlined,
    ThumbsUpDownOutlined,
    ThumbUp,
    ThumbUpOutlined,
} from '@mui/icons-material';

import { normalizeSubscribeStatus } from '../utils/subscribeStatus';

const OPTIONS = [
    {
        key: 'positive',
        status: true,
        Icon: ThumbUpOutlined,
        SelectedIcon: ThumbUp,
    },
    {
        key: 'negative',
        status: false,
        Icon: ThumbDownOutlined,
        SelectedIcon: ThumbDown,
    },
    {
        key: 'undecided',
        status: null,
        Icon: ThumbsUpDownOutlined,
        SelectedIcon: ThumbsUpDownOutlined,
    },
];

const optionForStatus = (status) => {
    const normalizedStatus = normalizeSubscribeStatus(status);
    return OPTIONS.find((option) => option.status === normalizedStatus) || OPTIONS[2];
};

const optionLabel = (option, currentOption, positiveLabel, selectedPositiveLabel, negativeLabel) => {
    if (option.status === true) {
        return option === currentOption ? selectedPositiveLabel : positiveLabel;
    }
    if (option.status === false) {
        return negativeLabel;
    }
    return 'Undecided';
};

const PreferenceStatusButtons = ({
    status,
    onStatusChange,
    variant = 'compact',
    positiveLabel = 'Follow',
    selectedPositiveLabel = 'Following',
    negativeLabel = 'Not for me',
    className = '',
    testId,
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const normalizedStatus = normalizeSubscribeStatus(status);
    const currentOption = optionForStatus(normalizedStatus);
    const isLabeled = variant === 'labeled';
    const CurrentIcon = currentOption.SelectedIcon;
    const currentLabel = optionLabel(
        currentOption,
        currentOption,
        positiveLabel,
        selectedPositiveLabel,
        negativeLabel,
    );

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const closeOnOutsideClick = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                setOpen(false);
            }
        };
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [open]);

    const selectStatus = (nextStatus) => () => {
        const normalizedNextStatus = normalizeSubscribeStatus(nextStatus);
        setOpen(false);
        if (normalizedNextStatus !== normalizedStatus) {
            onStatusChange(normalizedNextStatus);
        }
    };

    if (variant === 'thumbPair' || isLabeled) {
        return (
            <div
                className={[
                    'preferenceStatusThumbPair',
                    isLabeled ? 'preferenceStatusLabeledPair' : '',
                    className,
                ].filter(Boolean).join(' ')}
                data-testid={testId}
                role='group'
                aria-label='Preference'
            >
                {OPTIONS.slice(0, 2).map((option) => {
                    const isSelected = option.status === normalizedStatus;
                    const Icon = isSelected ? option.SelectedIcon : option.Icon;
                    const label = optionLabel(option, currentOption, positiveLabel, selectedPositiveLabel, negativeLabel);
                    return (
                        <button
                            key={option.key}
                            type='button'
                            className={['preferenceStatusThumbButton', isSelected ? 'preferenceStatusThumbButtonSelected' : ''].filter(Boolean).join(' ')}
                            aria-label={label}
                            aria-pressed={isSelected}
                            disabled={disabled}
                            onClick={selectStatus(isSelected ? null : option.status)}
                        >
                            <Icon className='preferenceStatusIcon' />
                            {isLabeled && <span className='preferenceStatusLabel'>{label}</span>}
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={[
                'preferenceStatusMenu',
                `preferenceStatusMenu-${variant}`,
                className,
            ].filter(Boolean).join(' ')}
            data-testid={testId}
        >
            <button
                type='button'
                className='preferenceStatusTrigger'
                aria-label={currentLabel}
                aria-haspopup='menu'
                aria-expanded={open}
                onClick={() => setOpen((wasOpen) => !wasOpen)}
            >
                <CurrentIcon className='preferenceStatusIcon' />
            </button>
            {open && (
                <div className='preferenceStatusPopover' role='menu'>
                    {OPTIONS.map((option) => {
                        const isSelected = option.status === normalizedStatus;
                        const Icon = isSelected ? option.SelectedIcon : option.Icon;
                        const label = optionLabel(option, currentOption, positiveLabel, selectedPositiveLabel, negativeLabel);
                        return (
                            <button
                                key={option.key}
                                type='button'
                                className={['preferenceStatusOption', isSelected ? 'preferenceStatusOptionSelected' : ''].filter(Boolean).join(' ')}
                                role='menuitemradio'
                                aria-checked={isSelected}
                                onClick={selectStatus(option.status)}
                            >
                                <Icon className='preferenceStatusOptionIcon' />
                                <span>{label}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default PreferenceStatusButtons;
